import React from 'react';
import { useQuery } from '@tanstack/react-query'; 
import { Box, Container, Grid, Typography } from "@mui/material";

// Components
import Socialmedia from '../components/Socialmedia';
import PageHeader from '../components/PageHeader';
import LinesDecor from '../components/LinesDecor';
import BlueLine from '../components/BlueLine';

// Assets
import missionbg from '../../assets/centrum_assets/design/about/mission.png';
import foundersImage from '../../assets/centrum_assets/design/about/ferrer.png';
import backgroundImage from '../../assets/images/home/bg2.png';

// API Client
import axiosClient from "axiosClient";

import { numToWords } from '../../utils/numberUtils'; 

const About = () => {
    // --- DATA FETCHING (React Query) ---
    const { data: stationData } = useQuery({
        queryKey: ['stationCount'], 
        queryFn: async () => {
            const response = await axiosClient.get('/stations/count');
            return response.data.data;
        },
    });

    // 1. Get the display string (Default to "160+")
    const stationCount = stationData?.formatted_count || "160+";

    // 2. Extract the raw number (removes "+" or other non-digits for calculation)
    const rawNumber = parseInt(stationCount.replace(/\D/g, ''), 10) || 160;

    // 3. Convert to words (e.g., "one-hundred-sixty")
    let stationCountWords = numToWords(rawNumber);

    // 4. Append "plus" if the original string had a "+"
    if (stationCount.includes('+')) {
        stationCountWords += " plus";
    }

    return (
        <>
            <PageHeader title="ABOUT" />
            
            {/* GET TO KNOW US SECTION */}
            <Box
                sx={{
                    backgroundImage: `url(${backgroundImage})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    overflow: "hidden",
                }}
            >
                <Container maxWidth="md" sx={{ py: 13 }}>
                    <Box sx={{ mx: 2.5 }}>
                        <Typography
                            variant="h5"
                            sx={{
                                color: "#0099FF",
                                fontWeight: 700,
                                textTransform: "uppercase",
                                mb: 1,
                            }}
                        >
                            Get to Know Us
                        </Typography>

                        <Typography
                            variant="h2"
                            sx={{
                                fontWeight: 800,
                                color: "#0D2040",
                                textTransform: "uppercase",
                                mb: 1,
                            }}
                        >
                            Fueled by Passion
                        </Typography>

                        <Typography
                            sx={{
                                fontSize: '2.5rem',
                                color: "#2B2B2B",
                                fontWeight: 400,
                                lineHeight: 1.6,
                                mb: 8
                            }}
                        >
                            Forged out of uncompromising passion, RKC, the Filipino family business is
                            leading the way. RKC's Continuing Evolution, Inspiring the Next Generation
                        </Typography>
                    </Box>

                    <Grid container spacing={4} alignItems="flex-start" >
                        <Grid item xs={12} md={6}>
                            <BlueLine height="15%" borderRadius="2px" width='1.5px' color="#00b5e2">
                                <Typography sx={{ mb: 4, fontSize: {lg: "1.5rem", xs: '1.7rem', md: '1.5rem'}, fontWeight: 550, color: "#6e6e6eff",lineHeight: 1.8, maxWidth: '90%', textAlign: 'justify'}}>
                                    Rael Kitz Corporation (RKC) was founded in 1978, by Spouses
                                    Renato and Milagros Ferrer of Mangatarem, Pangasinan.
                                    <br/><br/>
                                    RKC started as a dealer for a multinational oil company in their
                                    hometown, but it has since grown to be what is now a group of
                                    companies mainly dealing with the retail and distribution of
                                    petroleum products.
                                    <br/><br/>
                                    To date, RKC has grown substantially to establish {stationCountWords} ({stationCount}) gasoline stations in Northern and Central
                                    Luzon and counting.
                                </Typography>

                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "flex-start",
                                        flexWrap: "wrap",
                                        mt: 2,
                                        flexDirection: { xs: "column", md: "row" },
                                        textAlign: { xs: "left", md: "left" },
                                    }}
                                >
                                    <Typography
                                        variant="h1"
                                        sx={{ fontWeight: 700, color: "#7EC142", lineHeight: 1 }}
                                    >
                                        {stationCount}
                                    </Typography>

                                    <Typography
                                        sx={{ color: "#7EC142", fontSize: 14, maxWidth: { md: '50%' }, fontWeight: 'bold' }}
                                    >
                                        FUEL STATIONS IN NORTHERN LUZON
                                    </Typography>
                                </Box>
                            </BlueLine>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Box
                                component="img"
                                src={foundersImage}
                                alt="Founders"
                                sx={{
                                    width: "100%",
                                    objectFit: "cover",
                                    display: { xs: "none", md: "block" },
                                }}
                            />
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* MISSION SECTION */}
            <Box
                sx={{
                    py: { xs: 8, md: 12 },
                    backgroundImage: `url(${missionbg})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    overflow: "hidden",
                }}
            >
                <Container maxWidth="lg">
                    <Typography
                        variant="h2"
                        sx={{
                            fontWeight: 800,
                            color: "#0D2040",
                            textTransform: "uppercase",
                            mb: 2,
                            letterSpacing: 1
                        }}
                    >
                        Our Mission
                    </Typography>

                    <Typography
                        variant="h3"
                        sx={{
                            color: "#555",
                            lineHeight: 1.3,
                            px: 3
                        }}
                    >
                        Centrum Fuel is constantly working to provide innovative solutions for
                        the demands of fuel products and services. The company works hard to
                        provide opportunities for dealers to grow as it grows, while providing
                        sustainable income for the community.
                    </Typography>
                </Container>
            </Box>

            {/* SOCIAL MEDIA */}
            <Socialmedia />

            <Box sx={{ position: "Relative", width: "100%" }}>
                <LinesDecor greenMt={{ xl: -5, lg: -5, md: -4, sm: -2, xs: 1 }}blueMt={{ xl: 0, lg: 1, md: 2, sm: 1, xs: 3 }}/>
            </Box>
        </>
    );
};

export default About;